import { Navigate, useParams } from "react-router-dom";
import { Card, Flex } from "../../../components";
import { SignificantTokenTransfers } from "../../../components/SignificantTokenTransfers/SignificantTokenTransfers";
import { Compare } from "./components/Compare";
import { EntryStrategy } from "./components/EntryStrategy/EntryStrategy";
import { ExpandableCard } from "./components/ExpandableCard/ExpandableCard";
import { FundamentalAnalysis } from "./components/FundamentalAnalysis";
import { GatewayScoreAndSummary } from "./components/GatewayScoreAndSummary";
import { InvestmentConclusion } from "./components/InvestmentConclusion/InvestmentConclusion";
import { NonExpandableCard } from "./components/NonExpandableCard/NonExpandableCard";
import { ProsAndCons } from "./components/ProsAndCons";
import { RiskAssessment } from "./components/RiskAssessment";
import { SocialScore } from "./components/SocialScore";
import { TechnicalAnalysis } from "./components/TechnicalAnalysis/TechnicalAnalysis";
import { TokenDistribution } from "./components/TokenDistribution";
import { TokenInformation } from "./components/TokenInformation";
import { TradeToken } from "./components/TradeToken/TradeToken";
import { UnlockEvents } from "./components/UnlockEvents/UnlockEvents";
import { useFetchReport } from "./hooks/useFetchReport";
import "./SingleToken.scss";

export const SingleToken = () => {
  const { projectId } = useParams();
  const {
    data,
    isLoading,
    // error,
  } = useFetchReport(projectId);

  if (!projectId) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex gap-4 max-w-[1400px] mx-auto px-3 flex-col lg:flex-row mb-10 top-0 left-0">
      <div className="lg:sticky lg:top-0 lg:left-0 overflow-y-auto h-auto lg:h-screen no-scrollbar pt-32">
        <Flex
          grow={1}
          basis="0"
          direction="column"
          wrap="nowrap"
          gap={16}
          className="w-full lg:w-[calc(33%-1rem)] w-max-full lg:max-w-[466px] pr-0 lg:pr-2 "
        >
          <div className="flex flex-col md:flex-row lg:flex-col gap-4">
            <Flex grow={1}>
              <NonExpandableCard title="Token information">
                <TokenInformation projectId={projectId} />
              </NonExpandableCard>
            </Flex>
            <Flex justifyContent="center">
              <TradeToken />
            </Flex>
          </div>
          <div className="flex flex-col md:flex-row lg:flex-col gap-4">
            <Flex grow={1}>
              <ExpandableCard
                className="block md:hidden lg:block"
                title="Token distribution"
              >
                <TokenDistribution
                  projectId={projectId}
                  tokenSymbol={projectId.toString()}
                />
              </ExpandableCard>
              <NonExpandableCard
                className="hidden md:block lg:hidden"
                title="Token distribution"
              >
                <TokenDistribution
                  projectId={projectId}
                  tokenSymbol={projectId.toString()}
                />
              </NonExpandableCard>
            </Flex>
            <Flex>
              <ExpandableCard
                className="block md:hidden lg:block"
                title="Unlock events"
              >
                <UnlockEvents projectId={projectId} />
              </ExpandableCard>
              <NonExpandableCard
                className="hidden md:block lg:hidden"
                title="Unlock events"
              >
                <UnlockEvents projectId={projectId} />
              </NonExpandableCard>
            </Flex>
          </div>
          <div className="p-[20px] relative rounded-lg bg-[rgba(9,20,30,0.9)]">
            <SignificantTokenTransfers projectId={projectId} />
          </div>
        </Flex>
      </div>
      <Flex
        grow={2}
        basis="0"
        direction="column"
        wrap="nowrap"
        gap={16}
        className="flex-1 pt-32"
      >
        <Flex>
          <NonExpandableCard>
            <GatewayScoreAndSummary data={data} isLoading={isLoading} />
          </NonExpandableCard>
        </Flex>
        {/* <Flex>
          <NonExpandableCard>
            <ProjectInformation projectId={projectId} />
          </NonExpandableCard>
        </Flex> */}
        {/* <NonExpandableCard>
          <TokenPrices projectId={projectId} />
        </NonExpandableCard> */}
        <Flex>
          <ExpandableCard
            title="Technical analysis"
            gatewayScore={
              data?.detailedComponentAnalysis?.technicalAnalysis?.score || 0
            }
          >
            <TechnicalAnalysis projectData={data} />
          </ExpandableCard>
        </Flex>
        <Flex>
          <ExpandableCard
            title="Fundamental analysis"
            gatewayScore={
              data?.detailedComponentAnalysis?.fundamentalAnalysis?.score || 0
            }
          >
            <FundamentalAnalysis
              data={data?.detailedComponentAnalysis?.fundamentalAnalysis}
            />
          </ExpandableCard>
        </Flex>
        <Flex>
          <ExpandableCard
            title="Social score"
            gatewayScore={6.5}
            poweredByCookie
          >
            <SocialScore projectId={projectId} />
          </ExpandableCard>
        </Flex>
        <Flex>
          <ExpandableCard title="Pros and Cons">
            <ProsAndCons
              strengths={
                data?.investmentStrategyAndConclusion.prosAndCons.strengths
              }
              concerns={
                data?.investmentStrategyAndConclusion.prosAndCons.concerns
              }
              isLoading={isLoading}
            />
          </ExpandableCard>
        </Flex>
        <Flex>
          <ExpandableCard title="Risk Assessment">
            <RiskAssessment
              riskAssessmentData={
                data?.investmentStrategyAndConclusion?.riskAssessment
              }
            />
          </ExpandableCard>
        </Flex>
        <Flex>
          <ExpandableCard title="Entry Strategy and Position Management Plan">
            <EntryStrategy
              data={
                data?.investmentStrategyAndConclusion
                  ?.entryStrategyAndPositionManagement
              }
            />
          </ExpandableCard>
        </Flex>
        <Flex>
          <Card className="p-[30px]">
            <InvestmentConclusion
              investmentStrategyAndConclusion={
                data?.investmentStrategyAndConclusion
              }
              executiveSummary={data?.executiveSummary}
              isLoading={isLoading}
            />
          </Card>
        </Flex>
        <Flex>
          <Card>
            <Compare baseTokenId={projectId} />
          </Card>
        </Flex>
      </Flex>
    </div>
  );
};
